import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const SENSORY = '#2493A2';

interface NoiseDot {
  x: number;
  y: number;
  r: number;
  opacity: number;
}

interface EdgeSegment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface ObjectBox {
  x: number;
  y: number;
  w: number;
  h: number;
  label: string;
}

export function EdgeObjectSimplifier() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 320;
    const height = 180;

    // --- Phase 1: Random noise dots ---
    const noiseDots: NoiseDot[] = d3.range(200).map(() => ({
      x: Math.random() * width,
      y: Math.random() * height,
      r: Math.random() * 2 + 0.5,
      opacity: Math.random() * 0.6 + 0.2,
    }));

    const noiseGroup = svg.append('g').attr('class', 'noise');

    noiseGroup
      .selectAll('circle')
      .data(noiseDots)
      .enter()
      .append('circle')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', 0)
      .attr('fill', SENSORY)
      .attr('opacity', (d) => d.opacity)
      .transition()
      .duration(400)
      .delay((_, i) => i * 2)
      .attr('r', (d) => d.r);

    // --- Phase 2: Dots cluster into edge lines ---
    const boxes: ObjectBox[] = [
      { x: 30, y: 30, w: 70, h: 90, label: 'chair' },
      { x: 130, y: 20, w: 60, h: 130, label: 'person' },
      { x: 220, y: 50, w: 60, h: 100, label: 'door' },
    ];

    const edges: EdgeSegment[] = boxes.flatMap((b) => [
      { x1: b.x, y1: b.y, x2: b.x + b.w, y2: b.y },
      { x1: b.x + b.w, y1: b.y, x2: b.x + b.w, y2: b.y + b.h },
      { x1: b.x + b.w, y1: b.y + b.h, x2: b.x, y2: b.y + b.h },
      { x1: b.x, y1: b.y + b.h, x2: b.x, y2: b.y },
    ]);

    const edgeGroup = svg.append('g').attr('class', 'edges').attr('opacity', 0);

    edgeGroup
      .selectAll('line')
      .data(edges)
      .enter()
      .append('line')
      .attr('x1', (d) => d.x1)
      .attr('y1', (d) => d.y1)
      .attr('x2', (d) => d.x1)
      .attr('y2', (d) => d.y1)
      .attr('stroke', SENSORY)
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.7);

    // --- Phase 3: Labeled boxes ---
    const boxGroup = svg.append('g').attr('class', 'boxes').attr('opacity', 0);

    boxGroup
      .selectAll('rect')
      .data(boxes)
      .enter()
      .append('rect')
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('width', (d) => d.w)
      .attr('height', (d) => d.h)
      .attr('fill', 'none')
      .attr('stroke', SENSORY)
      .attr('stroke-width', 2)
      .attr('rx', 3);

    boxGroup
      .selectAll('text')
      .data(boxes)
      .enter()
      .append('text')
      .attr('x', (d) => d.x + d.w / 2)
      .attr('y', (d) => d.y + d.h + 16)
      .attr('text-anchor', 'middle')
      .attr('font-size', 11)
      .attr('font-weight', 600)
      .attr('class', 'viz-text')
      .text((d) => d.label);

    // --- Sequenced transitions ---
    const t1 = d3.timeout(() => {
      // Fade out noise, bring in edges
      noiseGroup
        .transition()
        .duration(500)
        .attr('opacity', 0.15);

      edgeGroup
        .transition()
        .duration(400)
        .attr('opacity', 1);

      edgeGroup
        .selectAll('line')
        .transition()
        .duration(500)
        .delay((_, i) => i * 40)
        .attr('x2', (d) => (d as EdgeSegment).x2)
        .attr('y2', (d) => (d as EdgeSegment).y2);
    }, 1200);

    const t2 = d3.timeout(() => {
      // Fade out noise fully, fade edges, bring in labeled boxes
      noiseGroup
        .transition()
        .duration(400)
        .attr('opacity', 0)
        .remove();

      edgeGroup
        .transition()
        .duration(400)
        .attr('opacity', 0.2);

      boxGroup
        .transition()
        .duration(500)
        .attr('opacity', 1);
    }, 2600);

    return () => {
      t1.stop();
      t2.stop();
      svg.selectAll('*').remove();
    };
  }, []);

  return (
    <div className="viz-container">
      <h4 className="viz-container__title">
        <i className="fa-solid fa-eye" /> Sight &mdash; Visual Categorization
      </h4>
      <svg ref={svgRef} viewBox="0 0 320 180" />
      <p className="viz-container__caption">
        Detail is discarded to produce objects.
      </p>
    </div>
  );
}
